// src/pages/OrderTracking.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, ArrowLeft, Package, User, Phone, MapPin, ShoppingBag, 
  AlertCircle, CheckCircle, Truck, Loader2, Home, Clock, Settings 
} from 'lucide-react';
import { ordersAPI, productsAPI } from '../services/api';
import './css/OrderTracking.css'; 

const OrderTracking = () => {
  const [code, setCode] = useState('');
  const [order, setOrder] = useState(null);
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Enhanced option formatter with better structure
  const formatOptionLabel = (group, value) => {
    const extraPrice = Number(value.extra_price) || 0;
    return {
      groupName: group.name,
      valueName: value.value,
      extraPrice: extraPrice,
      formatted: extraPrice > 0 ? `${group.name}: ${value.value} (+DT ${extraPrice.toFixed(2)})` : `${group.name}: ${value.value}`
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Veuillez saisir un code de suivi');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await ordersAPI.getOrderByTrackingCode(code.trim().toUpperCase());
      const orderData = response.data;

      // Enrich items with full product + option details
      const itemsWithOptions = orderData.items.filter(item => {
        try {
          const opts = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
          return opts && Object.keys(opts).length > 0;
        } catch {
          return false;
        }
      });

      let enriched = [...orderData.items];

      if (itemsWithOptions.length > 0) {
        const uniqueProductIds = [...new Set(itemsWithOptions.map(item => item.productId))];
        const productResponses = await Promise.allSettled(
          uniqueProductIds.map(pid => productsAPI.getById(pid))
        );

        const productsMap = {};
        productResponses.forEach((resp, idx) => {
          if (resp.status === 'fulfilled') {
            productsMap[uniqueProductIds[idx]] = resp.value.data;
          }
        });

        enriched = orderData.items.map(item => {
          let parsedOptions = {};
          try {
            parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
          } catch {
            parsedOptions = {};
          }

          const fullProduct = productsMap[item.productId];
          if (!fullProduct || Object.keys(parsedOptions).length === 0) {
            return {
              ...item,
              finalPrice: Number(item.price || 0),
              optionLabels: null,
              optionsStructured: null
            };
          }

          const basePrice = Number(fullProduct.salePrice || fullProduct.regularPrice || 0);
          let optionExtra = 0;
          const structuredOptions = [];

          fullProduct.options.forEach(group => {
            const selectedValueId = parsedOptions[group.id];
            if (selectedValueId) {
              const value = group.values.find(v => v.id === Number(selectedValueId));
              if (value) {
                const extra = Number(value.extra_price) || 0;
                optionExtra += extra;
                const formattedOption = formatOptionLabel(group, value);
                structuredOptions.push(formattedOption);
              }
            }
          });

          const finalPrice = basePrice + optionExtra;

          return {
            ...item,
            finalPrice,
            optionsStructured: structuredOptions.length > 0 ? structuredOptions : null,
            name: fullProduct.name || item.name,
            selected_options: parsedOptions
          };
        });
      } else {
        enriched = enriched.map(item => ({
          ...item,
          finalPrice: Number(item.price || 0),
          optionsStructured: null
        }));
      }

      setOrder(orderData);
      setEnrichedItems(enriched);
    } catch (err) {
      setError("Commande introuvable. Verifiez le code et reessayez.");
      setOrder(null);
      setEnrichedItems([]);
    }
    setLoading(false);
  };

  const handleNewTrack = () => {
    setOrder(null);
    setEnrichedItems([]);
    setCode('');
    setError('');
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { 
        icon: Clock, 
        color: '#f59e0b', 
        bg: '#fef3c7',
        label: 'En attente',
        description: 'Votre commande est en preparation'
      },
      processing: { 
        icon: Loader2, 
        color: '#f59e0b', 
        bg: '#fde68a',
        label: 'En cours',
        description: 'Votre commande est en cours de traitement'
      },
      shipped: { 
        icon: Truck, 
        color: '#10b981', 
        bg: '#d1fae5',
        label: 'Expediee',
        description: 'Votre commande est en route'
      },
      delivered: { 
        icon: CheckCircle, 
        color: '#059669', 
        bg: '#a7f3d0',
        label: 'Livree',
        description: 'Votre commande a ete livree'
      },
      cancelled: { 
        icon: AlertCircle, 
        color: '#ef4444', 
        bg: '#fee2e2',
        label: 'Annulee',
        description: 'Cette commande a ete annulee'
      },
    };
    return configs[status.toLowerCase()] || { 
      icon: AlertCircle, 
      color: '#6b7280', 
      bg: '#f3f4f6',
      label: 'Inconnu',
      description: 'Statut inconnu'
    };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Order Details View
  if (order && enrichedItems.length > 0) {
    const subtotal = enrichedItems.reduce((sum, item) => sum + item.finalPrice * item.quantity, 0);
    const discount = Number(order.discount_amount || 0);
    const deliveryFee = Number(order.delivery_fee || 0);
    const total = Math.max(0, subtotal - discount + deliveryFee);
    const { icon: StatusIcon, color, bg, label, description } = getStatusConfig(order.status);

    return (
      <div className="ot-container">
        {/* Header */}
        <div className="ot-header">
          <button onClick={() => navigate('/')} className="ot-back-button">
            <ArrowLeft size={20} />
            <span>Retour a l'accueil</span>
          </button>
        </div>

        {/* Status Banner */}
        <div className="ot-status-banner">
          <div className="ot-status-icon-wrapper" style={{ backgroundColor: bg }}>
            <StatusIcon 
              size={56} 
              className={order.status === 'processing' ? 'ot-status-icon-spin' : ''} 
              style={{ color }} 
            />
          </div>
          <h1 className="ot-status-title" style={{ color }}>{label}</h1>
          <p className="ot-status-description">{description}</p>
        </div>

        <div className="ot-details-view">
          {/* Order Details Section */}
          <div className="ot-details-section">
            <div className="ot-details-card">
              <div className="ot-card-header">
              <h2 className="ot-card-title">Details de la commande</h2>
              </div>
              <div className="ot-details-grid">
                <div className="ot-detail-item">
                  <span className="ot-detail-label">Commande</span>
                  <span className="ot-detail-value">#{order.id}</span>
                </div>
                <div className="ot-detail-item">
                  <span className="ot-detail-label">Code de suivi</span>
                  <span className="ot-detail-value">{order.trackingCode}</span>
                </div>
                <div className="ot-detail-item">
                  <span className="ot-detail-label">Date</span>
                  <span className="ot-detail-value">{formatDate(order.createdAt)}</span>
                </div>
                <div className="ot-detail-item">
                  <span className="ot-detail-label">Statut</span>
                  <span className="ot-detail-value">
                    <span className="ot-status-badge" style={{ backgroundColor: bg, color }}>
                      {label}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div className="ot-items-section">
            <div className="ot-items-card">
              <div className="ot-card-header">
                <div className="ot-header-flex">
                  <div className="ot-header-left">
                    <ShoppingBag size={20} style={{ color: 'var(--reyza-red)' }} />
                    <h2 className="ot-card-title">Articles de la commande</h2>
                  </div>
                  <span className="ot-items-count">
                    {enrichedItems.length} {enrichedItems.length === 1 ? 'article' : 'articles'}
                  </span>
                </div>
              </div>

              <div className="ot-items-list">
                {enrichedItems.map((item) => {
                  const itemTotal = item.finalPrice * item.quantity;

                  return (
                    <div key={item.id} className="ot-item">
                      <div className="ot-item-image-wrapper">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="ot-item-image"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="ot-item-placeholder">
                            <Package size={32} />
                          </div>
                        )}
                      </div>

                      <div className="ot-item-content">
                        <div className="ot-item-header">
                          <div className="ot-item-details">
                            <h3 className="ot-item-name">{item.name}</h3>

                            <div className="ot-item-meta">
                              <span className="ot-item-quantity">
                                <Package size={14} /> Qté : {item.quantity}
                              </span>
                              <span className="ot-item-price">
                                DT {item.finalPrice.toFixed(2)} l'unite
                              </span>
                            </div>
                          </div>

                          <div className="ot-item-total">
                            DT {itemTotal.toFixed(2)}
                          </div>
                        </div>

                        {/* Enhanced Options Display */}
                        {item.optionsStructured && item.optionsStructured.length > 0 && (
                          <div className="ot-item-options">
                            <div className="ot-options-header">
                              <Settings size={14} />
                              <span>Options selectionnees</span>
                            </div>
                            <div className="ot-options-list">
                              {item.optionsStructured.map((opt, idx) => (
                                <div key={idx} className="ot-option-tag">
                                  <span>
                                    <strong>{opt.groupName}:</strong>
                                    <span className="ot-option-separator"> </span>
                                    {opt.valueName}
                                  </span>
                                  {opt.extraPrice > 0 && (
                                    <span className="ot-option-extra-price">
                                      +DT {opt.extraPrice.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className="ot-summary-section">
            <div className="ot-summary-card">
              <div className="ot-card-header">
                <h2 className="ot-card-title">Resume de la commande</h2>
              </div>

              <div className="ot-summary-content">
                <div className="ot-summary-row">
                  <span className="ot-summary-label">Sous-total</span>
                  <span className="ot-summary-value">DT {subtotal.toFixed(2)}</span>
                </div>

                {discount > 0 && (
                  <div className="ot-summary-row ot-discount-row">
                    <span className="ot-summary-label">
                      Remise
                      {order.promo_code && (
                        <span className="ot-promo-badge">{order.promo_code}</span>
                      )}
                    </span>
                    <span className="ot-summary-value ot-discount-value">-DT {discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="ot-summary-row">
                  <span className="ot-summary-label">Livraison</span>
                  <span className="ot-summary-value ot-delivery-value">
                    {deliveryFee === 0 ? 'Gratuite' : `DT ${deliveryFee.toFixed(2)}`}
                  </span>
                </div>

                <div className="ot-summary-divider"></div>

                <div className="ot-summary-row ot-summary-total">
                  <span className="ot-summary-label-total">Total</span>
                  <span className="ot-summary-amount">DT {total.toFixed(2)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="ot-actions">
                <button onClick={handleNewTrack} className="ot-button ot-button-primary">
                  <Search size={20} />
                  <span>Suivre une autre commande</span>
                </button>
                <button onClick={() => navigate('/')} className="ot-button ot-button-secondary">
                  <Home size={20} />
                  <span>Retour a la boutique</span>
                </button>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="ot-customer-section">
            <div className="ot-customer-card">
              <div className="ot-card-header">
                <h2 className="ot-card-title">Informations client</h2>
              </div>
              <div className="ot-customer-grid">
                <div className="ot-customer-item">
                  <User size={18} className="ot-customer-icon" />
                  <div>
                    <span className="ot-customer-label">Nom</span>
                    <span className="ot-customer-value">{order.firstName} {order.lastName}</span>
                  </div>
                </div>
                <div className="ot-customer-item">
                  <Phone size={18} className="ot-customer-icon" />
                  <div>
                    <span className="ot-customer-label">Telephone</span>
                    <span className="ot-customer-value">{order.phoneNumber}</span>
                  </div>
                </div>
                <div className="ot-customer-item">
                  <MapPin size={18} className="ot-customer-icon" />
                  <div>
                    <span className="ot-customer-label">Adresse</span>
                    <span className="ot-customer-value">{order.shippingAddress}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tracking Form View
  return (
    <div className="ot-container">
      {/* Header */}
      <div className="ot-header">
        <button onClick={() => navigate('/')} className="ot-back-button">
          <ArrowLeft size={20} />
            <span>Retour a l'accueil</span>
        </button>
      </div>

      {/* Hero Section */}
      <div className="ot-hero-section">
        <div className="ot-hero-icon-wrapper">
          <Package size={56} className="ot-hero-icon" />
        </div>
        <h1 className="ot-hero-title">Suivre votre commande</h1>
        <p className="ot-hero-subtitle">
          Saisissez votre code de suivi a 12 caracteres pour voir le statut et les details de votre commande.
        </p>
      </div>

      {/* Tracking Form Card */}
      <div className="ot-form-container">
        <div className="ot-card ot-form-card">
          <form onSubmit={handleSubmit} className="ot-form" noValidate>
            <div className="ot-form-group">
              <label className="ot-label">Code de suivi</label>
              <div className="ot-input-wrapper">
                <Search size={20} className="ot-input-icon" />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g., A1B2C3D4E5F6"
                  className="ot-input"
                  maxLength={12}
                  disabled={loading}
                  aria-label="Code de suivi"
                  autoComplete="off"
                />
              </div>
            </div>

            {error && (
              <div className="ot-error-message" role="alert">
                <AlertCircle size={18} className="ot-error-icon" />
                <span>{error}</span>
              </div>
            )}

            <button 
              type="submit" 
              className="ot-submit-button" 
              disabled={loading || !code.trim()}
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="ot-loading-spinner" />
                  <span>Suivi...</span>
                </>
              ) : (
                <>
                  <Search size={20} />
                  <span>Suivre la commande</span>
                </>
              )}
            </button>

            <div className="ot-help-box">
              <p className="ot-help-text">
                <strong>Besoin d'aide ?</strong> Votre code de suivi se trouve dans l'email de confirmation.
              </p>
            </div>
          </form>
        </div>

        {/* Info Cards */}
        <div className="ot-info-cards">
          <div className="ot-info-card">
            <div className="ot-info-card-icon">
              <Package size={24} />
            </div>
            <h3 className="ot-info-card-title">Ou trouver mon code ?</h3>
            <p className="ot-info-card-text">
              Verifiez l'email de confirmation pour votre code de suivi a 12 caracteres.
            </p>
          </div>

          <div className="ot-info-card">
            <div className="ot-info-card-icon">
              <Clock size={24} />
            </div>
            <h3 className="ot-info-card-title">Mises a jour en temps reel</h3>
            <p className="ot-info-card-text">
              Suivez le statut de votre commande en temps reel, du traitement a la livraison.
            </p>
          </div>

          <div className="ot-info-card">
            <div className="ot-info-card-icon">
              <Truck size={24} />
            </div>
            <h3 className="ot-info-card-title">Livraison rapide</h3>
            <p className="ot-info-card-text">
              Nous vous tenons informes a chaque etape jusqu'a la livraison.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
